import {
  Link,
} from 'react-router';
import {
  DateTime,
} from 'luxon';
import i18n from 'i18next';
import * as R from 'ramda';

import {
  WorkoutEvent,
} from '@gymcloud/core';

import {
  Avatar,
} from 'web-components';

import {
  Flex,
  Text,
} from 'web-styled';

import Tab from '../../Tab';
import * as S from './styled';

const DAY_FORMAT = 'EEEE, MMMM d';
const formatDate = (isoDay: string, f: string) => DateTime.fromISO(isoDay).toFormat(f);

const getPercent = (value: number) => {
  const percent = 100 * value;
  const number = Number(`${Math.round(`${percent}e2`)}e-2`) || 0;
  return `${number}%`;
};

interface Props {
  features: {
    results: boolean
  },
  workoutEvents: {
    [key: string]: WorkoutEvent[]
  },
  missedWorkouts: any[]
  clientsPerformance: any[]
}

const Tables = ({
  features,
  workoutEvents,
  missedWorkouts,
  clientsPerformance,
}: Props) => {
  const tabs = [
    {
      id: 1,
      name: i18n.t('dashboard.workout.this.week'),
      tooltip: i18n.t('tooltip.dashboard.workout.this.week'),
    },
    {
      id: 2,
      name: i18n.t('dashboard.client.performance'),
      tooltip: i18n.t('tooltip.dashboard.client.performance'),
    },
    {
      id: 3,
      name: i18n.t('dashboard.missed.workouts'),
      tooltip: i18n.t('tooltip.dashboard.missed.workouts'),
    },
  ];

  const ThisWeek = () => (
    <S.Day>
      {Object.keys(workoutEvents).map(day => (
        <div key={day}>
          <Flex padding="0 0 0 21px">
            <Text
              size="16px"
              color="#404447"
              weight="700"
            >
              {formatDate(day, DAY_FORMAT)}
            </Text>
          </Flex>
          <table>
            <thead>
              <tr>
                <th>{i18n.format(i18n.t('client'), 'cap')}</th>
                <th>{`Performed ${i18n.t('workout')}`}</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {workoutEvents[day].map(ev => (
                <tr key={ev.id}>
                  <td>
                    <Link to={`/users/${ev.person_id}`}>
                      <S.LinkLabel>
                        <Avatar
                          src={ev.person_avatar}
                          width="24px"
                          height="24px"
                          placeholderSize={13}
                        />
                        <span>
                          {ev.person_name}
                        </span>
                      </S.LinkLabel>
                    </Link>
                  </td>
                  <td>
                    {features.results ? (
                      <Link to={`/events/${ev.id}`}>
                        <S.LinkLabel missed={ev.is_completed ? '0' : '1'}>
                          {ev.workout_name}
                        </S.LinkLabel>
                      </Link>
                    ) : (
                      <S.LinkLabel missed={ev.is_completed ? '0' : '1'}>
                        {ev.workout_name}
                      </S.LinkLabel>
                    )}
                  </td>
                  <td>{formatDate(ev.begins_at, 'T')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </S.Day>
  );

  const ClientPerformance = () => (
    <table>
      <thead>
        <tr>
          <th>{i18n.format(i18n.t('client'), 'cap')}</th>
          <th>Last Week</th>
          <th>This Week</th>
        </tr>
      </thead>
      <tbody>
        {clientsPerformance.map(client => (
          <tr key={client.id}>
            <td>
              <Link to={`/users/${client.id}/programs`}>
                <S.LinkLabel>
                  <Avatar
                    src={client.avatar_thumb_url}
                    width="24px"
                    height="24px"
                    placeholderSize={13}
                  />
                  <span>
                    {client.full_name}
                  </span>
                </S.LinkLabel>
              </Link>
            </td>
            <td>
              {getPercent(client.last_week_performance)}
            </td>
            <td>
              <S.DynamicCounter
                positive={client.last_week_performance < client.this_week_performance}
              >
                {getPercent(client.this_week_performance)}
                {client.last_week_performance < client.this_week_performance ? (
                  <S.PositiveDynamic />
                ) : (
                  <S.NegativeDynamic />
                )}
              </S.DynamicCounter>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const MissedWorkouts = () => (
    <table>
      <thead>
        <tr>
          <th>{i18n.format(i18n.t('client'), 'cap')}</th>
          <th>{i18n.t('workout.missed')}</th>
          <th>Time</th>
        </tr>
      </thead>
      <tbody>
        {missedWorkouts.map(ev => (
          <tr key={ev.id}>
            <td>
              <Link to={`/users/${ev.person_id}/programs`}>
                <S.LinkLabel>
                  <Avatar
                    src={ev.person_avatar}
                    width="24px"
                    height="24px"
                    placeholderSize={13}
                  />
                  <span>
                    {ev.person_name}
                  </span>
                </S.LinkLabel>
              </Link>
            </td>
            <td>
              {features.results ? (
                <Link to={`/events/${ev.id}`}>
                  <S.LinkLabel missed="1">
                    {ev.workout_name}
                  </S.LinkLabel>
                </Link>
              ) : (
                <S.LinkLabel>
                  {ev.workout_name}
                </S.LinkLabel>
              )}
            </td>
            <td>{formatDate(ev.begins_at, 'T')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <Tab tabs={tabs}>
      {activeTab => ({
        1: R.isEmpty(workoutEvents)
          ? (
            <S.EmptyList>
              {i18n.t('dashboard.table.empty', { name: '$t(workouts)' })}
            </S.EmptyList>
          ) : (
            <ThisWeek />
          ),
        2: (clientsPerformance.length > 0) ? (
          <ClientPerformance />
        ) : (
          <S.EmptyList>
            No results to display
          </S.EmptyList>
        ),
        3: (missedWorkouts.length > 0) ? (
          <MissedWorkouts />
        ) : (
          <S.EmptyList>
            {`No missed ${i18n.t('workouts')} this week`}
          </S.EmptyList>
        ),
      }[activeTab])}
    </Tab>
  );
};

export default Tables;
